import React from "react";
import { RSD } from "../../../lib/api";
import { Card } from "./OrderAnalytics";

const pct = (v) => (v === null || v === undefined ? "—" : `${(v * 100).toFixed(1)}%`);
const DAYS = ["Pon", "Uto", "Sre", "Čet", "Pet", "Sub", "Ned"];
const NO_DATA = "Nema podataka za izabrani period.";

function Table({ cols, rows, testid }) {
  if (!rows?.length) return <p className="bg-white rounded-2xl border p-3 text-sm text-slate-400">{NO_DATA}</p>;
  return (
    <div className="bg-white rounded-2xl border overflow-x-auto" data-testid={testid}>
      <table className="w-full text-sm min-w-[560px]">
        <thead><tr className="text-left text-[10px] uppercase tracking-wider text-slate-400">
          {cols.map((c) => <th key={c.key} className="p-2.5 font-black">{c.label}</th>)}
        </tr></thead>
        <tbody className="divide-y">
          {rows.map((r) => (
            <tr key={r.product_id || r.shift_id || r.key}>
              {cols.map((c) => <td key={c.key} className="p-2.5">{c.fmt ? c.fmt(r[c.key], r) : (r[c.key] ?? "—")}</td>)}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

export function RestaurantFunnelTab({ d }) {
  const f = d.restaurant_funnel;
  const rows = [["Prikazi kartice", f.card_impressions], ["Klikovi na karticu", f.card_clicks],
                ["Pregledi strane", f.page_views], ["Dodato u korpu", f.add_to_cart],
                ["Checkout", f.checkout], ["Završene (sesije)", f.completed_sessions]];
  return (
    <div className="space-y-3" data-testid="ra-funnel">
      <div className="bg-white rounded-2xl border divide-y">
        {rows.map(([l, v]) => (
          <div key={l} className="p-3 flex items-baseline justify-between text-sm">
            <span className="font-bold">{l}</span><span className="font-display font-black">{v}</span>
          </div>
        ))}
      </div>
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
        <Card label="CTR kartice" value={pct(f.ctr)} testid="ra-ctr" />
        <Card label="Strana → korpa" value={pct(f.view_to_cart)} testid="ra-view-cart" />
        <Card label="Strana → porudžbina" value={pct(f.view_to_order)} testid="ra-view-order" />
        <Card label="Korpa → završeno" value={pct(f.cart_to_completed)} testid="ra-cart-completed" />
      </div>
      <div className="bg-white rounded-2xl border divide-y text-sm" data-testid="ra-funnel-business">
        <p className="p-3 text-xs font-black uppercase tracking-wider text-slate-400">Poslovne porudžbine (Ponesi)</p>
        <div className="p-3 flex justify-between"><span className="text-slate-500">Kreirano</span><b>{f.business_orders.orders_created}</b></div>
        <div className="p-3 flex justify-between"><span className="text-slate-500">Završeno</span><b>{f.business_orders.orders_completed}</b></div>
        <div className="p-3 flex justify-between"><span className="text-slate-500">Sa vezom na sesiju</span><b>{f.business_orders.orders_with_session_attribution}</b></div>
      </div>
      <p className="text-[11px] text-slate-400">Bihevioralni koraci se mere u jedinstvenim sesijama i dostupni su od uvođenja first-party analitike. Vista je izuzeta.</p>
    </div>
  );
}

export function TimeTab({ d }) {
  const maxH = Math.max(1, ...d.by_hour.map((h) => h.orders));
  const maxC = Math.max(1, ...d.heatmap.map((c) => c.orders));
  const cell = (v) => (v ? `rgba(226,29,37,${0.15 + 0.85 * (v / maxC)})` : "#f1f5f9");
  return (
    <div className="space-y-4" data-testid="ra-time">
      <div>
        <p className="text-xs font-black uppercase tracking-wider text-slate-400 mb-1">Porudžbine po satima</p>
        <div className="bg-white rounded-2xl border p-3">
          <div className="flex items-end gap-1 h-28">
            {d.by_hour.map((h) => (
              <div key={h.hour} className="flex-1 flex flex-col justify-end h-full" title={`${h.hour}:00 · ${h.orders} · ${RSD(h.revenue)}`}>
                <div style={{ height: `${(h.orders / maxH) * 100}%` }} className={`w-full rounded-t min-h-[2px] ${h.orders ? "bg-brand-red" : "bg-slate-100"}`} />
              </div>
            ))}
          </div>
          <div className="flex gap-1 mt-1">{d.by_hour.map((h) => <span key={h.hour} className="flex-1 text-center text-[8px] text-slate-400">{h.hour}</span>)}</div>
        </div>
      </div>
      <div>
        <p className="text-xs font-black uppercase tracking-wider text-slate-400 mb-1">Prodaja po danu nedelje</p>
        <div className="bg-white rounded-2xl border divide-y" data-testid="ra-weekday">
          {d.by_weekday.map((w) => (
            <div key={w.weekday} className="p-2.5 flex items-baseline justify-between text-sm">
              <span className="font-semibold">{DAYS[w.weekday]}</span>
              <span className="text-slate-500">{w.orders} · {RSD(w.revenue)}</span>
            </div>
          ))}
        </div>
      </div>
      <div>
        <p className="text-xs font-black uppercase tracking-wider text-slate-400 mb-1">Heatmap dan × sat (Europe/Belgrade)</p>
        <div className="bg-white rounded-2xl border p-3 overflow-x-auto" data-testid="ra-heatmap">
          <div className="min-w-[560px]">
            {[0, 1, 2, 3, 4, 5, 6].map((day) => (
              <div key={day} className="flex items-center gap-1 mb-1">
                <span className="w-8 text-[10px] font-bold text-slate-400">{DAYS[day]}</span>
                {Array.from({ length: 24 }, (_, h) => {
                  const c = d.heatmap.find((x) => x.weekday === day && x.hour === h) || { orders: 0, revenue: 0 };
                  return <div key={h} title={`${DAYS[day]} ${h}:00 · ${c.orders} · ${RSD(c.revenue)}`}
                    style={{ background: cell(c.orders) }} className="flex-1 h-4 rounded-[3px]" />;
                })}
              </div>
            ))}
            <div className="flex gap-1 pl-8">{Array.from({ length: 24 }, (_, h) => <span key={h} className="flex-1 text-center text-[8px] text-slate-400">{h}</span>)}</div>
          </div>
        </div>
      </div>
    </div>
  );
}

export function ProductsTab({ d }) {
  const p = d.products;
  const cols = [
    { key: "name", label: "Proizvod" }, { key: "impressions", label: "Prikazi" },
    { key: "views", label: "Pregledi" }, { key: "add_to_cart", label: "U korpu" },
    { key: "units_sold", label: "Prodato" },
    { key: "completed_revenue", label: "Promet", fmt: (v) => RSD(v) },
    { key: "purchase_conversion", label: "Konverzija", fmt: pct },
  ];
  return (
    <div className="space-y-4" data-testid="ra-products">
      {[["Najprodavaniji", p.best_sellers, "ra-best"], ["Najmanje prodavani", p.worst_sellers, "ra-worst"],
        ["Najviše pregledani", p.most_viewed, "ra-most-viewed"]].map(([title, rows, tid]) => (
        <div key={tid}>
          <p className="text-xs font-black uppercase tracking-wider text-slate-400 mb-1">{title}</p>
          <Table cols={cols} rows={rows} testid={tid} />
        </div>
      ))}
      <div>
        <p className="text-xs font-black uppercase tracking-wider text-slate-400 mb-1">Visok pregled / niska prodaja</p>
        {!p.high_views_low_sales.length
          ? <p className="bg-white rounded-2xl border p-3 text-sm text-slate-400">Nema dovoljno podataka.</p>
          : (
            <div className="bg-white rounded-2xl border divide-y" data-testid="ra-high-low">
              {p.high_views_low_sales.map((r) => (
                <div key={r.product_id} className="p-3 text-sm">
                  <p className="font-bold">{r.name}</p>
                  <p className="text-slate-500">{r.views} pregleda · {r.purchasing_sessions} porudžbina · {pct(r.purchase_conversion)} konverzija</p>
                  <p className="text-[11px] text-slate-400 mt-0.5">
                    {r.signal === "HIGH_VIEWS_LOW_SALES" ? p.note : "Rani signal — potencijalno niska konverzija (malo pregleda za pouzdan zaključak)."}
                  </p>
                </div>
              ))}
            </div>
          )}
      </div>
    </div>
  );
}

export function VistaTab({ d }) {
  const v = d.vista;
  const maxH = Math.max(1, ...v.by_hour.map((h) => h.orders));
  return (
    <div className="space-y-3" data-testid="ra-vista">
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
        <Card label="Vista promet" value={RSD(v.completed_revenue)} testid="ra-vista-revenue" />
        <Card label="Vista porudžbine" value={v.completed_orders} testid="ra-vista-orders" />
        <Card label="Prosečna Vista" value={RSD(v.avg_order_value)} testid="ra-vista-aov" />
        <Card label="Artikala / porudžbini" value={v.items_per_order} testid="ra-vista-items" />
      </div>
      <div className="grid grid-cols-2 gap-2">
        <Card label="Gotovina" value={RSD(v.by_payment.CASH.completed_revenue)} sub={`${v.by_payment.CASH.completed_orders} porudžbina`} testid="ra-vista-cash" />
        <Card label="Kartica" value={RSD(v.by_payment.CARD.completed_revenue)} sub={`${v.by_payment.CARD.completed_orders} porudžbina`} testid="ra-vista-card" />
      </div>
      <div className="bg-white rounded-2xl border p-3">
        <p className="text-xs font-black uppercase tracking-wider text-slate-400 mb-2">Vista prodaja po satima</p>
        <div className="flex items-end gap-1 h-24">
          {v.by_hour.map((h) => (
            <div key={h.hour} className="flex-1 flex flex-col justify-end h-full" title={`${h.hour}:00 · ${h.orders} · ${RSD(h.revenue)}`}>
              <div style={{ height: `${(h.orders / maxH) * 100}%` }} className={`w-full rounded-t min-h-[2px] ${h.orders ? "bg-slate-800" : "bg-slate-100"}`} />
            </div>
          ))}
        </div>
      </div>
      <div>
        <p className="text-xs font-black uppercase tracking-wider text-slate-400 mb-1">Porudžbine po smeni</p>
        <Table testid="ra-vista-shifts" rows={v.orders_per_shift}
          cols={[{ key: "shift_id", label: "Smena" }, { key: "orders", label: "Porudžbine" },
                 { key: "revenue", label: "Promet", fmt: (x) => RSD(x) }]} />
      </div>
    </div>
  );
}
